import { useSearchParams } from 'react-router-dom';

import { Icon } from '~/components';
import {
  EASING_FUNCTIONS,
  SEARCH_PARAM_KEY,
  TRANSITION_DURATIONS,
  WHITELISTED_DOMAINS,
} from '~/components/ScrollCollapse/debug/transition';
import { useScrollCollapseContext } from '~/components/ScrollCollapse/hooks/useScrollCollapseContext';
import { cn } from '~/lib/cn';

export type EasingFunction = (typeof EASING_FUNCTIONS)[number];
export type TransitionDuration = (typeof TRANSITION_DURATIONS)[number];

const SCROLL_DISTANCE_LABEL = 'Scroll Distance';
const CURRENT_PX_LABEL = 'Current (px)';
const MAX_PX_LABEL = 'Maximum (px)';
const SCROLL_DISTANCE_CURRENT_LABEL = (
  <>
    {SCROLL_DISTANCE_LABEL}
    <br />
    {CURRENT_PX_LABEL}
  </>
);
const SCROLL_DISTANCE_MAX_LABEL = (
  <>
    {SCROLL_DISTANCE_LABEL}
    <br />
    {MAX_PX_LABEL}
  </>
);
const EASING_FUNCTION_LABEL = 'Easing Function';
const TRANSITION_DURATION_LABEL = 'Transition Duration (ms)';

/**
 * This is a TEMPORARY COMPONENT that is exposed in the development environment only.
 * It provides options to control the scroll collapse behavior for different stakeholders to tinker
 * with the implementation around transition easing, duration and scroll distance.
 *
 * @TODO WMG1-573: Delete this temporary component once we've finalized the scroll collapse behavior.
 */
export function ScrollCollapseControls() {
  const [searchParams] = useSearchParams();
  const {
    scrollDistance,
    scrollDistanceMax,
    setScrollDistanceMax,
    easingFunction,
    setEasingFunction,
    transitionDuration,
    setTransitionDuration,
  } = useScrollCollapseContext();

  // Only available in the development environment with the "header" search param present.
  if (
    !WHITELISTED_DOMAINS.includes(window.location.hostname) ||
    searchParams.get(SEARCH_PARAM_KEY) === null
  ) {
    return null;
  }

  return (
    <div
      className="absolute right-xl top-md z-30 flex items-end gap-md"
      aria-label="ScrollCollapseControls"
    >
      <div className="flex flex-col gap-xs">
        <label className="pl-xs text-body-xs">
          {SCROLL_DISTANCE_CURRENT_LABEL}
        </label>
        <input
          type="number"
          value={parseFloat(scrollDistance.toFixed(2)).toString()}
          disabled
          readOnly
          className={cn(
            'w-[100px] cursor-not-allowed rounded-sm border-px px-sm py-xs outline-none duration-300 bg-transparent border-emphasis-subtle text-subtle',
            {
              'text-error-default': scrollDistance <= scrollDistanceMax,
              'text-success-default': scrollDistance > scrollDistanceMax,
            },
          )}
        />
      </div>
      <div className="flex flex-col gap-xs">
        <label className="pl-xs text-body-xs">
          {SCROLL_DISTANCE_MAX_LABEL}
        </label>
        <input
          type="number"
          value={scrollDistanceMax}
          min="1"
          max="500"
          className="w-[100px] rounded-sm border-px px-sm py-xs outline-none duration-300 bg-transparent border-emphasis-subtle hover:border-emphasis-stronger"
          onChange={({ target: { value } }) => {
            setScrollDistanceMax?.(Number(value));
          }}
        />
      </div>
      <div className="flex flex-col gap-xs">
        <label className="pl-xs text-body-xs">
          {EASING_FUNCTION_LABEL}
          <a
            href="https://curveeditor.com/"
            target="_blank"
            rel="noreferrer"
            className="ml-sm"
          >
            <Icon type="external-link" size="xs" variant="light" />
          </a>
        </label>
        <select
          className="h-[34px] w-[150px] rounded-sm border-px px-sm py-xs outline-none duration-300 bg-transparent border-emphasis-subtle hover:border-emphasis-stronger"
          defaultValue={easingFunction}
          onChange={({ target: { value } }) => {
            setEasingFunction?.(value as EasingFunction);
          }}
        >
          {EASING_FUNCTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-xs">
        <label className="pl-xs text-body-xs">
          {TRANSITION_DURATION_LABEL}
        </label>
        <select
          className="h-[34px] w-[150px] rounded-sm border-px px-sm py-xs outline-none duration-300 bg-transparent border-emphasis-subtle hover:border-emphasis-stronger"
          defaultValue={transitionDuration}
          onChange={({ target: { value } }) => {
            setTransitionDuration?.(value as TransitionDuration);
          }}
        >
          {TRANSITION_DURATIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

ScrollCollapseControls.displayName = 'ScrollCollapseControls';
